import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Alert, Select, Modal, Input, message, Tooltip } from 'antd';
import { Typography } from 'antd';
import { EditOutlined, ReloadOutlined, ThunderboltOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Project } from '../types';
import bedrockService from '../services/bedrockService';
import projectService from '../services/projectService';
import { extractErrorMessage } from '../utils/errorHandler';

const { Text } = Typography;
const { TextArea } = Input;

interface GeneratedCodeTabContentProps {
  project: Project;
  frontendTechStack: 'React' | 'Angular';
  fullstackTechStack: 'React-Node' | 'Angular-Node';
  loadingStates: {
    fullstack: boolean;
    frontend: boolean;
    backend: boolean;
    database: boolean;
  };
  errorStates: {
    fullstack: string | null;
    frontend: string | null;
    backend: string | null;
    database: string | null;
  };
  onFrontendTechStackChange: (value: 'React' | 'Angular') => void;
  onFullstackTechStackChange: (value: 'React-Node' | 'Angular-Node') => void;
  onGenerateFullstackCode: () => void;
  onGenerateFrontendCode: () => void;
  onGenerateBackendCode: () => void;
  onGenerateDatabaseSchema: () => void;
  onProjectUpdate?: (updatedProject: Project) => void;
  canEdit?: boolean;
  canCreate?: boolean;
  databaseApproved?: boolean;
  onApproveDatabaseChange?: () => void;
  fullstackApproved?: boolean;
  onApproveFullstackChange?: () => void;
  onSyncStages?: (originStage: string, prompt: string, singleEditCallback: () => void | Promise<void>, closeCallback?: () => void) => void;
}

interface CodeView {
  key: string;
  title: string;
  description: string;
  urlKey: string;
  techStack: string | null;
  onTechStackChange: ((value: any) => void) | null;
  onGenerate: () => void;
  techOptions: Array<{ label: string; value: string }> | null;
}

const GeneratedCodeTabContent: React.FC<GeneratedCodeTabContentProps> = ({
  project,
  frontendTechStack,
  fullstackTechStack,
  loadingStates,
  errorStates,
  onFrontendTechStackChange,
  onFullstackTechStackChange,
  onGenerateFullstackCode,
  onGenerateFrontendCode,
  onGenerateBackendCode,
  onGenerateDatabaseSchema,
  onProjectUpdate,
  canEdit = false,
  canCreate = false,
  databaseApproved = false,
  onApproveDatabaseChange,
  fullstackApproved = false,
  onApproveFullstackChange,
  onSyncStages
}) => {
  // State for edit modal (fullstack)
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editPrompt, setEditPrompt] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // State for test case generation
  const [testCaseLoading, setTestCaseLoading] = useState(false);

  // State for edit modal (database schema)
  const [editDatabaseModalVisible, setEditDatabaseModalVisible] = useState(false);
  const [editDatabasePrompt, setEditDatabasePrompt] = useState('');
  const [editDatabaseLoading, setEditDatabaseLoading] = useState(false);

  // State for regenerate confirmation modal
  const [regenerateModalVisible, setRegenerateModalVisible] = useState(false);
  const [regenerateView, setRegenerateView] = useState<{ key: string; title: string; onGenerate: () => void } | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [hasStartedLoading, setHasStartedLoading] = useState(false);

  // Watch for loading state changes to close modal when regeneration completes
  useEffect(() => {
    if (isRegenerating && regenerateView) {
      const isLoading = loadingStates[regenerateView.key as keyof typeof loadingStates];
      const errorState = errorStates[regenerateView.key as keyof typeof errorStates];
      
      // Track when loading starts
      if (isLoading && !hasStartedLoading) {
        setHasStartedLoading(true);
      }
      
      // Close modal when loading completes (only after it has started)
      if (hasStartedLoading && !isLoading) {
        // Show success or error message
        if (errorState) {
          message.error(`Failed to regenerate ${regenerateView.title.toLowerCase()}. ${errorState}`);
        } else {
          message.success(`${regenerateView.title} regenerated successfully!`);
        }
        
        setRegenerateModalVisible(false);
        setRegenerateView(null);
        setIsRegenerating(false);
        setHasStartedLoading(false);
      }
    }
  }, [loadingStates, errorStates, isRegenerating, regenerateView, hasStartedLoading]);

  // State for continue generation (2nd LLM pass)
  const [continueLoading, setContinueLoading] = useState(false);

  // Handle continue fullstack generation (2nd LLM call)
  const handleContinueGeneration = async () => {
    if (!project?.id) {
      message.error('Project ID is missing');
      return;
    }
    setContinueLoading(true);
    try {
      const response = await bedrockService.continueFullstackGeneration(project.id);
      if (response && response.status === 'success' && response.data) {
        const updatedProject = {
          ...project,
          artifacts: {
            ...project.artifacts,
            code: {
              ...project.artifacts.code,
              fullstackRepositoryUrl: response.data.downloadUrl,
              needsContinuation: false,
              isEdited: true,
              editedTimestamp: response.data.editedTimestamp,
            }
          }
        };
        if (onProjectUpdate) {
          onProjectUpdate(updatedProject);
        }
        message.success('Code generation enhanced successfully!');
      } else {
        const errorMsg = response?.message || 'Failed to continue generation. No valid response received.';
        message.error(errorMsg);
      }
    } catch (error) {
      console.error('Error continuing fullstack generation:', error);
      const errorMessage = extractErrorMessage(error);
      message.error(errorMessage);
    } finally {
      setContinueLoading(false);
    }
  };

  // Handle generate test cases
  const handleGenerateTestCases = async () => {
    if (!project?.id) {
      message.error('Project ID is missing');
      return;
    }
    setTestCaseLoading(true);
    try {
      const response = await bedrockService.generateFullstackTestCases(project.id);
      if (response && response.status === 'success' && response.data && response.data.mergedCodeDownloadUrl && response.data.testcaseDownloadUrl) {
        const updatedProject = {
          ...project,
          artifacts: {
            ...project.artifacts,
            code: {
              ...project.artifacts.code,
              fullstackRepositoryUrl: response.data.mergedCodeDownloadUrl,
              testcaseDownloadUrl: response.data.testcaseDownloadUrl,
            }
          }
        };
        if (onProjectUpdate) {
          onProjectUpdate(updatedProject);
        }
        message.success('Test cases generated successfully!');
      } else {
        const errorMsg = response?.message || 'Failed to generate test cases. No valid response received.';
        message.error(errorMsg);
      }
    } catch (error) {
      console.error('Error generating test cases:', error);
      const errorMessage = extractErrorMessage(error);
      message.error(errorMessage);
    } finally {
      setTestCaseLoading(false);
    }
  };

  // Handle edit fullstack code
  const handleEditFullstackCode = async () => {
    if (!editPrompt.trim()) {
      message.error('Please enter a valid prompt');
      return;
    }
    setEditLoading(true);
    try {
      const response = await bedrockService.editFullstackCode(project.id, editPrompt.trim());
      if (response && response.status === 'success' && response.data && response.data.downloadUrl && response.data.s3Key) {
        // Refetch project from backend to get the updated data that was saved to database
        const updatedProjectResponse = await projectService.getProjectById(project.id);
        if (updatedProjectResponse && updatedProjectResponse.data && onProjectUpdate) {
          onProjectUpdate(updatedProjectResponse.data);
        }
        message.success('Fullstack code edited successfully!');
        handleModalClose();
      } else {
        const errorMsg = response?.message || 'Failed to edit fullstack code. No valid response received.';
        message.error(errorMsg);
      }
    } catch (error) {
      console.error('Error editing fullstack code:', error);
      const errorMessage = extractErrorMessage(error);
      message.error(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  // Handle edit database schema
  const handleEditDatabaseSchema = async () => {
    if (!editDatabasePrompt.trim()) {
      message.error('Please enter a valid prompt');
      return;
    }
    setEditDatabaseLoading(true);
    try {
      const response = await bedrockService.editDatabaseSchema(project.id, editDatabasePrompt.trim());
      if (response && response.status === 'success' && response.data && response.data.downloadUrl && response.data.rawSqlS3Key) {
        // Refetch project from backend to get the updated data that was saved to database
        const updatedProjectResponse = await projectService.getProjectById(project.id);
        console.log('Refetched project after database edit:', {
          databaseIsEdited: updatedProjectResponse?.data?.artifacts?.code?.databaseIsEdited,
          databaseEditedTimestamp: updatedProjectResponse?.data?.artifacts?.code?.databaseEditedTimestamp,
          fullCodeArtifact: updatedProjectResponse?.data?.artifacts?.code
        });
        if (updatedProjectResponse && updatedProjectResponse.data && onProjectUpdate) {
          onProjectUpdate(updatedProjectResponse.data);
        }
        message.success('Database schema edited successfully!');
        handleDatabaseModalClose();
      } else {
        const errorMsg = response?.message || 'Failed to edit database schema. No valid response received.';
        message.error(errorMsg);
      }
    } catch (error: any) {
      console.error('Error editing database schema:', error);
      
      // Show detailed error message from backend
      const errorMessage = extractErrorMessage(error);
      
      message.error(errorMessage);
    } finally {
      setEditDatabaseLoading(false);
    }
  };

  // Handle modal close (fullstack)
  const handleModalClose = () => {
    setEditModalVisible(false);
    setEditPrompt('');
  };
  // Handle modal close (database)
  const handleDatabaseModalClose = () => {
    setEditDatabaseModalVisible(false);
    setEditDatabasePrompt('');
  };

  // Handle edit button click (fullstack)
  const handleEditButtonClick = () => {
    setEditModalVisible(true);
  };
  // Handle edit button click (database)
  const handleEditDatabaseButtonClick = () => {
    setEditDatabaseModalVisible(true);
  };

  // Handle regenerate button click
  const handleRegenerateClick = (view: CodeView) => {
    setRegenerateView({ key: view.key, title: view.title, onGenerate: view.onGenerate });
    setRegenerateModalVisible(true);
  };

  // Handle regenerate confirmation
  const handleRegenerateConfirm = () => {
    if (regenerateView) {
      setIsRegenerating(true);
      
      // If regenerating database, clear fullstack-related data from project
      if (regenerateView.key === 'database' && onProjectUpdate) {
        const updatedProject = {
          ...project,
          artifacts: {
            ...project.artifacts,
            code: {
              ...project.artifacts.code,
              fullstackRepositoryUrl: undefined,
              testcaseDownloadUrl: undefined,
              isEdited: false,
              editedTimestamp: undefined,
              techStack: undefined,
            }
          }
        };
        onProjectUpdate(updatedProject);
      }
      
      // If regenerating fullstack, clear edit and testcase related data from project
      if (regenerateView.key === 'fullstack' && onProjectUpdate) {
        const updatedProject = {
          ...project,
          artifacts: {
            ...project.artifacts,
            code: {
              ...project.artifacts.code,
              testcaseDownloadUrl: undefined,
              isEdited: false,
              editedTimestamp: undefined,
            }
          }
        };
        onProjectUpdate(updatedProject);
      }
      
      regenerateView.onGenerate();
      // Don't close modal immediately - will close when loading completes
    }
  };

  // Handle regenerate modal close
  const handleRegenerateModalClose = () => {
    if (!isRegenerating) {
      setRegenerateModalVisible(false);
      setRegenerateView(null);
      setIsRegenerating(false);
      setHasStartedLoading(false);
    }
  };

  const codeViews: CodeView[] = [
    {
      key: 'database',
      title: 'Database Schema',
      description: 'PostgreSQL',
      urlKey: 'databaseRepositoryUrl',
      techStack: null,
      onTechStackChange: null,
      onGenerate: onGenerateDatabaseSchema,
      techOptions: null,
    },
    {
      key: 'fullstack',
      title: 'Fullstack Code',
      description: 'Complete fullstack application',
      urlKey: 'fullstackRepositoryUrl',
      techStack: fullstackTechStack,
      onTechStackChange: onFullstackTechStackChange,
      onGenerate: onGenerateFullstackCode,
      techOptions: [
        { label: 'React-Node', value: 'React-Node' },
        { label: 'Angular-Node', value: 'Angular-Node' },
      ],
    },
    // {
    //   key: 'frontend',
    //   title: 'Frontend Code',
    //   description: 'React/Angular with TypeScript',
    //   urlKey: 'frontendRepositoryUrl',
    //   techStack: frontendTechStack,
    //   onTechStackChange: onFrontendTechStackChange,
    //   onGenerate: onGenerateFrontendCode,
    //   techOptions: [
    //     { label: 'React', value: 'React' },
    //     { label: 'Angular', value: 'Angular' },
    //   ],
    // },
    // {
    //   key: 'backend',
    //   title: 'Backend Code',
    //   description: 'Node.js with TypeScript',
    //   urlKey: 'backendRepositoryUrl',
    //   techStack: null,
    //   onTechStackChange: null,
    //   onGenerate: onGenerateBackendCode,
    //   techOptions: null,
    // },
  ];

  return (
    <div>
      <h3 style={{ marginBottom: '20px', color: '#111827', fontSize: '18px', fontWeight: 600 }}>
        Generate Code
      </h3>
      <Row gutter={[0, 16]}>
        {codeViews.map((view) => {
          const codeUrl = project.artifacts.code?.[view.urlKey as keyof typeof project.artifacts.code];
          
          const errorState = errorStates[view.key as keyof typeof errorStates];
          const loadingState = loadingStates[view.key as keyof typeof loadingStates];
          
          // Determine the tech stack value to display
          let displayTechStack = view.techStack;
          
          // If code is generated and techStack is available from backend, use it
          if (codeUrl && view.techOptions && project.artifacts.code?.techStack) {
            displayTechStack = project.artifacts.code.techStack;
          }

          return (
            <Col xs={24} key={view.key}>
              <Card
                title={view.title}
                extra={
                  view.techOptions ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Select
                        value={displayTechStack}
                        style={{ width: 140 }}
                        onChange={codeUrl ? undefined : (view.onTechStackChange || undefined)}
                        options={view.techOptions}
                        size="small"
                        disabled={!!codeUrl || loadingStates.database}
                      />
                    </div>
                  ) : (
                    <Text type="secondary">{view.description}</Text>
                  )
                }
              >
                {errorState && (
                  <Alert message={errorState} type="error" showIcon style={{ marginBottom: 8 }} />
                )}
                {codeUrl ? (
                  <div>
                    {/* Fullstack with needsContinuation: show ONLY the Continue Generation button */}
                    {view.key === 'fullstack' && project.artifacts.code?.needsContinuation ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Tooltip title="Please click the button to continue code generation." placement="bottom">
                          <Button
                            type="primary"
                            icon={<ThunderboltOutlined />}
                            onClick={handleContinueGeneration}
                            loading={continueLoading}
                            size="large"
                            style={{
                              backgroundColor: '#1890ff',
                              borderColor: '#1890ff'
                            }}
                          >
                            {continueLoading ? 'Generating...' : 'Continue Generation...'}
                          </Button>
                        </Tooltip>
                      </div>
                    ) : (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Button
                            type="link"
                            href={typeof codeUrl === 'string' ? codeUrl : undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ padding: 0, height: 'auto', textAlign: 'left' }}
                          >
                            {view.key === 'database' ? 'Download Database Schema' : 'Download Source Code'}
                          </Button>
                          {view.key === 'fullstack' && !project.artifacts.code?.testcaseDownloadUrl && canCreate && (
                            <Tooltip title="Generate Test Cases For Fullstack Source Code" placement="bottom">
                              <Button
                                type="default"
                                size="small"
                                onClick={handleGenerateTestCases}
                                loading={testCaseLoading}
                                style={{
                                  fontSize: '12px',
                                  height: '28px'
                                }}
                              >
                                Generate Test Case
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                        {view.key === 'fullstack' && project.artifacts.code?.isEdited && project.artifacts.code?.editedTimestamp && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Text style={{ 
                              fontSize: '11px', 
                              color: '#6b7280',
                              fontStyle: 'italic'
                            }}>
                              Last edited: {new Date(project.artifacts.code.editedTimestamp).toLocaleString()}
                            </Text>
                          </div>
                        )}
                        {view.key === 'database' && project.artifacts.code?.databaseIsEdited && project.artifacts.code?.databaseEditedTimestamp && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Text style={{ 
                              fontSize: '11px', 
                              color: '#6b7280',
                              fontStyle: 'italic'
                            }}>
                              Last edited: {new Date(project.artifacts.code.databaseEditedTimestamp).toLocaleString()}
                            </Text>
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {view.key === 'fullstack' && (
                          <>
                            {!fullstackApproved && canEdit && (
                              <Tooltip title="Approve fullstack code">
                                <Button
                                  type="primary"
                                  icon={<CheckCircleOutlined />}
                                  onClick={onApproveFullstackChange}
                                  style={{
                                    backgroundColor: '#28a745',
                                    borderColor: '#28a745',
                                    color: '#ffffff'
                                  }}
                                />
                              </Tooltip>
                            )}
                            {fullstackApproved && (
                              <Tooltip title="Fullstack code approved">
                                <Button
                                  type="default"
                                  icon={<CheckCircleOutlined />}
                                  disabled
                                  style={{
                                    borderColor: '#28a745',
                                    color: '#28a745'
                                  }}
                                />
                              </Tooltip>
                            )}
                            {canEdit && (
                              <Tooltip title="Edit fullstack code">
                                <Button
                                  type="default"
                                  icon={<EditOutlined />}
                                  onClick={handleEditButtonClick}
                                  style={{
                                    backgroundColor: '#dc3545',
                                    borderColor: '#dc3545',
                                    color: '#ffffff'
                                  }}
                                />
                              </Tooltip>
                            )}
                          </>
                        )}
                        {view.key === 'database' && (
                          <>
                            {!databaseApproved && canEdit && (
                              <Tooltip title="Approve database schema">
                                <Button
                                  type="primary"
                                  icon={<CheckCircleOutlined />}
                                  onClick={onApproveDatabaseChange}
                                  style={{
                                    backgroundColor: '#28a745',
                                    borderColor: '#28a745',
                                    color: '#ffffff'
                                  }}
                                />
                              </Tooltip>
                            )}
                            {databaseApproved && (
                              <Tooltip title="Database approved">
                                <Button
                                  type="default"
                                  icon={<CheckCircleOutlined />}
                                  disabled
                                  style={{
                                    borderColor: '#28a745',
                                    color: '#28a745'
                                  }}
                                />
                              </Tooltip>
                            )}
                            {canEdit && (
                              <Tooltip title="Edit database schema">
                                <Button
                                  type="default"
                                  icon={<EditOutlined />}
                                  onClick={handleEditDatabaseButtonClick}
                                  style={{
                                    backgroundColor: '#dc3545',
                                    borderColor: '#dc3545',
                                    color: '#ffffff'
                                  }}
                                />
                              </Tooltip>
                            )}
                            {canCreate && (
                              <Tooltip title={databaseApproved ? "Cannot regenerate approved database" : `Regenerate ${view.title}`}>
                                <Button
                                  type="default"
                                  icon={<ReloadOutlined />}
                                  onClick={() => handleRegenerateClick(view)}
                                  loading={loadingState}
                                  disabled={databaseApproved}
                                  style={{
                                    backgroundColor: '#1890ff',
                                    borderColor: '#1890ff',
                                    color: '#ffffff'
                                  }}
                                />
                              </Tooltip>
                            )}
                          </>
                        )}
                        
                        {/* Regenerate Button Code Block for non-database views */}
                        {view.key !== 'database' && canCreate && (
                          <Tooltip title={fullstackApproved && view.key === 'fullstack' ? "Cannot regenerate approved fullstack code" : `Regenerate ${view.title}`}>
                            <Button
                              type="default"
                              icon={<ReloadOutlined />}
                              onClick={() => handleRegenerateClick(view)}
                              loading={loadingState}
                              disabled={fullstackApproved && view.key === 'fullstack'}
                              style={{
                                backgroundColor: '#1890ff',
                                borderColor: '#1890ff',
                                color: '#ffffff'
                              }}
                            />
                          </Tooltip>
                        )}
                        </div>
                    </div>
                    )}
                  </div>
                ) : (
                  <Tooltip 
                    title={
                      view.key === 'fullstack' && !project.artifacts.code?.databaseRepositoryUrl 
                        ? "Please Generate Database Schema" 
                        : view.key === 'fullstack' && !databaseApproved
                        ? "Please approve the Database Schema first"
                        : view.key === 'fullstack' && loadingStates.database
                        ? "Database is being regenerated. Please wait..."
                        : ""
                    }
                    placement="bottom"
                  >
                    {canCreate && (
                      <Button
                        type="primary"
                        onClick={view.onGenerate}
                        loading={loadingState}
                        disabled={
                          (view.key === 'fullstack' && !project.artifacts.code?.databaseRepositoryUrl) ||
                          (view.key === 'fullstack' && !databaseApproved) ||
                          (view.key === 'fullstack' && loadingStates.database)
                        }
                        style={{
                          backgroundColor: 
                            (view.key === 'fullstack' && !project.artifacts.code?.databaseRepositoryUrl) || 
                            (view.key === 'fullstack' && !databaseApproved) ||
                            (view.key === 'fullstack' && loadingStates.database)
                              ? '#f0f0f0' 
                              : '#dc3545',
                          borderColor: 
                            (view.key === 'fullstack' && !project.artifacts.code?.databaseRepositoryUrl) || 
                            (view.key === 'fullstack' && !databaseApproved) ||
                            (view.key === 'fullstack' && loadingStates.database)
                              ? '#d9d9d9' 
                              : '#dc3545',
                          color: 
                            (view.key === 'fullstack' && !project.artifacts.code?.databaseRepositoryUrl) || 
                            (view.key === 'fullstack' && !databaseApproved) ||
                            (view.key === 'fullstack' && loadingStates.database)
                              ? '#bfbfbf' 
                              : '#ffffff',
                          cursor: 
                            (view.key === 'fullstack' && !project.artifacts.code?.databaseRepositoryUrl) || 
                            (view.key === 'fullstack' && !databaseApproved) ||
                            (view.key === 'fullstack' && loadingStates.database)
                              ? 'not-allowed' 
                              : 'pointer',
                          opacity: 
                            (view.key === 'fullstack' && !project.artifacts.code?.databaseRepositoryUrl) || 
                            (view.key === 'fullstack' && !databaseApproved) ||
                            (view.key === 'fullstack' && loadingStates.database)
                              ? 0.6 
                              : 1
                        }}
                      >
                        Generate {view.title}
                      </Button>
                    )}
                  </Tooltip>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Edit Fullstack Code Modal */}
      <Modal
        title="Edit Fullstack Code"
        open={editModalVisible}
        onCancel={editLoading ? undefined : handleModalClose}
        closable={!editLoading}
        maskClosable={!editLoading}
        footer={[
          <Button 
            key="cancel" 
            onClick={handleModalClose}
            disabled={editLoading}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={editLoading}
            onClick={() => {
              if (onSyncStages && editPrompt.trim()) {
                onSyncStages('fullstack', editPrompt.trim(), () => {
                  return handleEditFullstackCode();
                }, () => {
                  handleModalClose();
                });
              } else {
                handleEditFullstackCode();
              }
            }}
            disabled={!editPrompt.trim()}
            style={{
              backgroundColor: !editPrompt.trim() ? '#f5f5f5' : '#dc3545',
              borderColor: !editPrompt.trim() ? '#d9d9d9' : '#dc3545',
              color: !editPrompt.trim() ? '#bfbfbf' : '#ffffff',
              cursor: !editPrompt.trim() ? 'not-allowed' : 'pointer',
              opacity: !editPrompt.trim() ? 0.6 : 1
            }}
          >
            Submit
          </Button>
        ]}
        width={600}
      >
        <div>
          <p style={{ marginBottom: 16 }}>
            Please enter your editing instructions below. Describe what changes you want to make to the fullstack code.
          </p>
          <TextArea
            placeholder="Enter your editing prompt here... (e.g., 'Add user authentication', 'Implement dark mode', 'Add email notifications', etc.)"
            value={editPrompt}
            onChange={(e) => setEditPrompt(e.target.value)}
            rows={6}
            style={{ marginBottom: 16 }}
          />
          <p style={{ fontSize: '12px', color: '#666' }}>
            <strong>Tip:</strong> Be specific about the changes you want. The AI will modify the existing code based on your instructions.
          </p>
        </div>
      </Modal>

      {/* Edit Database Schema Modal */}
      <Modal
        title="Edit Database Schema"
        open={editDatabaseModalVisible}
        onCancel={editDatabaseLoading ? undefined : handleDatabaseModalClose}
        closable={!editDatabaseLoading}
        maskClosable={!editDatabaseLoading}
        footer={[
          <Button 
            key="cancel" 
            onClick={handleDatabaseModalClose}
            disabled={editDatabaseLoading}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={editDatabaseLoading}
            onClick={() => {
              if (onSyncStages && editDatabasePrompt.trim()) {
                onSyncStages('database', editDatabasePrompt.trim(), () => {
                  return handleEditDatabaseSchema();
                }, () => {
                  handleDatabaseModalClose();
                });
              } else {
                handleEditDatabaseSchema();
              }
            }}
            disabled={!editDatabasePrompt.trim()}
            style={{
              backgroundColor: !editDatabasePrompt.trim() ? '#f5f5f5' : '#1890ff',
              borderColor: !editDatabasePrompt.trim() ? '#d9d9d9' : '#1890ff',
              color: !editDatabasePrompt.trim() ? '#bfbfbf' : '#ffffff',
              cursor: !editDatabasePrompt.trim() ? 'not-allowed' : 'pointer',
              opacity: !editDatabasePrompt.trim() ? 0.6 : 1
            }}
          >
            Submit
          </Button>
        ]}
        width={600}
      >
        <div>
          <p style={{ marginBottom: 16 }}>
            Please enter your editing instructions below. Describe what changes you want to make to the database schema.
          </p>
          <TextArea
            placeholder="Enter your editing prompt here... (e.g., 'Add new table for audit logs', 'Change user table to include last_login', 'Remove deprecated fields', etc.)"
            value={editDatabasePrompt}
            onChange={(e) => setEditDatabasePrompt(e.target.value)}
            rows={6}
            style={{ marginBottom: 16 }}
            disabled={editDatabaseLoading}
          />
          <p style={{ fontSize: '12px', color: '#666' }}>
            <strong>Tip:</strong> Be specific about the changes you want. The AI will modify the existing schema based on your instructions.
          </p>
        </div>
      </Modal>

      {/* Regenerate Confirmation Modal */}
      <Modal
        title="Confirm Regeneration"
        open={regenerateModalVisible}
        onCancel={isRegenerating ? undefined : handleRegenerateModalClose}
        closable={!isRegenerating}
        maskClosable={!isRegenerating}
        footer={[
          <Button 
            key="cancel" 
            onClick={handleRegenerateModalClose}
            disabled={isRegenerating}
          >
            No
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger
            loading={isRegenerating}
            onClick={handleRegenerateConfirm}
          >
            Yes
          </Button>
        ]}
        width={500}
      >
        <div style={{ padding: '10px 0' }}>
          <p style={{ fontSize: '14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <strong>Regenerate {regenerateView?.title}?</strong>
          </p>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: regenerateView?.key === 'database' || regenerateView?.key === 'fullstack' ? 12 : 0 }}>
            Would you like to regenerate the {regenerateView?.title.toLowerCase()}? The existing {regenerateView?.title.toLowerCase()} will be overwritten.
          </p>
          {regenerateView?.key === 'database' && (
            <p style={{ fontSize: '14px', color: '#dc3545', fontWeight: 500, marginBottom: 0 }}>
              <strong>Note:</strong> Regenerating the database schema will also delete the existing fullstack code. You will need to regenerate the fullstack code after this operation.
            </p>
          )}
          {regenerateView?.key === 'fullstack' && (
            <p style={{ fontSize: '14px', color: '#dc3545', fontWeight: 500, marginBottom: 0 }}>
              <strong>Note:</strong> Regenerating the fullstack code will delete any edits and test cases. They will need to be regenerated after this operation.
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default GeneratedCodeTabContent;